import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useWatch } from 'react-hook-form';
import { capitalize as _capitalize, isEmpty as _isEmpty } from 'lodash';

import { ReactComponent as ChevronDown } from 'assets/icons/chevron-down.svg';
import { ReactComponent as ChevronUp } from 'assets/icons/chevron-up.svg';

import CountrySelect from 'components/CountrySelect';
import Input from 'components/Input';
import { FIELD_NAMES } from './BillingAddress.schema';
import { billingAddress as TEXT } from 'locales/en/cart';

import { saveBillingAddress, setBillingAddressSameAsShipping } from 'redux/slices/checkoutForm';
import { getStoreColors } from '../../helpers';
import { setShippingMethod } from 'redux/slices/cart';
import { MinimizedBillingAddress } from './MinimizedBillingAddress';
import { NonFundraiserBillingAddress } from './NonFundraiserBillingAddress';
import { terminalShippingMethodsSelector } from 'redux/slices/javelinStore';
import {
  previewStripeTerminalOrder,
  setCanPreviewStripeTerminalOrder,
  setShippingAddressDisabledState,
} from 'redux/slices/checkout';

const BillingAddress = ({ control, register, errors, setValue, minimized, reset, storeId, onChangeClick, watch }) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(true);
  const form = useWatch({
    control,
  });

  const isBillingSameAsShipping = form[FIELD_NAMES.billingAddressSameAsShipping];

  useEffect(() => {

    if (isBillingSameAsShipping) {
      const payload = {
        ...form,
        [FIELD_NAMES.billing_name]: form[FIELD_NAMES.name],
        [FIELD_NAMES.billing_address_1]: form[FIELD_NAMES.address_1],
        [FIELD_NAMES.billing_address_2]: form[FIELD_NAMES.address_2],
        [FIELD_NAMES.billing_postcode]: form[FIELD_NAMES.postcode],
        [FIELD_NAMES.billing_country]: form[FIELD_NAMES.country],
        [FIELD_NAMES.billing_city]: form[FIELD_NAMES.city],
        [FIELD_NAMES.billing_state]: form[FIELD_NAMES.state],
        [FIELD_NAMES.state]: form[FIELD_NAMES.state]
      }

      dispatch(saveBillingAddress(payload));
      reset(payload);
    } else {
      dispatch(saveBillingAddress(form));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetBillingSameAsShipping = (isChecked) => {
    setValue(FIELD_NAMES.billingAddressSameAsShipping, isChecked);
    dispatch(setBillingAddressSameAsShipping(isChecked));

    if (isChecked) {
      // if isChecked then make the same billing address as shipping
      setValue(FIELD_NAMES.billing_name, form[FIELD_NAMES.name]);
      setValue(FIELD_NAMES.billing_address_1, form[FIELD_NAMES.address_1]);
      setValue(FIELD_NAMES.billing_address_2, form[FIELD_NAMES.address_2]);
      setValue(FIELD_NAMES.billing_postcode, form[FIELD_NAMES.postcode]);
      setValue(FIELD_NAMES.billing_country, form[FIELD_NAMES.country]);
      setValue(FIELD_NAMES.billing_city, form[FIELD_NAMES.city]);
    }
  }

  const {
    checkoutStep,
    loading: checkoutLoading,
  } = useSelector(state => state.checkout);

  const handleStripeOrderPreview = _ => {

    if (checkoutStep === 1 || checkoutLoading) {
      return;
    }

    const requiredFields = [
      FIELD_NAMES.name, FIELD_NAMES.address_1,
      FIELD_NAMES.postcode, FIELD_NAMES.country, FIELD_NAMES.city,
      FIELD_NAMES.state, FIELD_NAMES.shippingMethod,
    ];

    const hasFormError = !_isEmpty(errors);
    const hasEmptyField = requiredFields.some(field => _isEmpty(form[field]));

    if (hasFormError || hasEmptyField) {
      dispatch(setCanPreviewStripeTerminalOrder(false));
      dispatch(setShippingAddressDisabledState(false));
      return;
    }

    dispatch(previewStripeTerminalOrder({ store: storeId, form }));
  }

  useEffect(() => {
    handleStripeOrderPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, checkoutLoading, minimized]);


  const handleFormChange = (e) => {
    dispatch(saveBillingAddress(form));
  };

  const onClickExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const handleShippingMethodChange = (type) => {
    dispatch(setShippingMethod(type));
    setValue(FIELD_NAMES.shippingMethod, type);
  }

  const containsNonFundraiserProducts = useSelector(state => state.cart.containsNonFundraiserProducts);

  const terminalShippingMethods = useSelector(terminalShippingMethodsSelector);

  if (minimized) {
    return (
      <MinimizedBillingAddress
        form={form}
        onClickChange={onChangeClick}
      />
    );
  }

  return (
    <>
      <div
        className="flex justify-between items-center"
        onClick={onClickExpand}
      >
        <h2
          data-testid="billing-title"
          className="text-javelin-gray-dark text-sm lg:font-bold lg:text-2xl mb-1"
        >
          {containsNonFundraiserProducts ? TEXT.title_shipping : TEXT.title}
        </h2>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 my-auto text-javelin-blue-dark fill-current cursor-pointer lg:hidden" />
        ) : (
          <ChevronDown className="w-4 h-4 my-auto text-javelin-blue-dark fill-current cursor-pointer lg:hidden" />
        )}
      </div>
      <div
        className={`lg:block lg:h-full transition-height ease-in-out ${isExpanded ? 'overflow-visible' : 'h-0 overflow-hidden'
          }`}
      >
        <form
          noValidate
          onChange={handleFormChange}
          className={`flex flex-col justify-between mt-2 lg:mt-0 pb-6 lg:pb-2 ${getStoreColors()}`}
        >
          <div
            className={`mx-0.5 lg:mx-0 transition-height ease-in-out pt-2 overflow-visible'`}
          >
            <Input
              label={
                containsNonFundraiserProducts ? TEXT.company_name : TEXT.name
              }
              name={FIELD_NAMES.name}
              register={register}
              required
              error={errors.name?.message}
              data-testid="name"
            />
            <Input
              label={TEXT.address}
              name={FIELD_NAMES.address_1}
              register={register}
              required
              error={errors.address_1?.message}
              maxLength={40}
              data-testid="address_1"
            />
            <Input
              name={FIELD_NAMES.address_2}
              register={register}
              className="mt-1"
              error={errors.address_2?.message}
              maxLength={40}
              data-testid="address_2"
            />
            <div className="grid lg:grid-cols-2 gap-x-4 lg:gap-x-4">
              <Input
                label={TEXT.postcode}
                name={FIELD_NAMES.postcode}
                register={register}
                required
                error={errors.postcode?.message}
                data-testid="postcode"
              />
              <CountrySelect
                name={FIELD_NAMES.country}
                label={TEXT.country}
                control={control}
                isDisabled={containsNonFundraiserProducts}
                required
                error={errors[FIELD_NAMES.country]?.name?.message}
              />
              {containsNonFundraiserProducts && (
                <>
                  <Input
                    name={FIELD_NAMES.city}
                    label={TEXT.city}
                    register={register}
                    required
                    error={errors.city?.message}
                    data-testid="city"
                  />
                  <Input
                    name={FIELD_NAMES.state}
                    label={TEXT.state}
                    register={register}
                    required
                    error={errors.state?.message}
                    data-testid="state"
                  />
                </>
              )}
            </div>
          </div>
          {containsNonFundraiserProducts && (
            <>
              <div className='flex justify-between md:mt-2 lg:mt-1 xxs:flex-col xxs:gap-2' >
                <span className='text-md text-javelin-gray-dark font-bold' >
                  Billing Address:
                </span>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input appearance-none h-4 w-4 border border-gray-300 rounded-sm bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer"
                    type="checkbox"
                    id="sameBillingAsShipping"
                    defaultChecked={isBillingSameAsShipping}
                    onChange={({ target }) => handleSetBillingSameAsShipping(target.checked)}
                  />
                  <label className="form-check-label inline-block text-gray-800" htmlFor="sameBillingAsShipping">
                    Same as Shipping Address
                  </label>
                </div>
              </div>
              {!isBillingSameAsShipping && (
                <NonFundraiserBillingAddress
                  control={control}
                  errors={errors}
                  register={register}
                  form={form}
                />
              )}
              <p className='text-xs text-javelin-gray-dark text-opacity-30 mt-2' >
                Orders can't be shipped to PO boxes. Signature required on delivery if order value is more than GBP 400 or USD 500 or EUR 400
              </p>
              <div className='flex justify-between mt-6 flex-row xxs:items-start xxs:gap-2 mb-3' >
                <span className='text-md text-javelin-gray-dark font-bold' >
                  Shipping Type:
                </span>
                <span className='text-md text-javelin-gray-dark font-bold' >
                  Estimated Delivery:
                </span>
              </div>
              <div className="flex flex-col">
                {!!terminalShippingMethods.length && terminalShippingMethods.map((shippingMethod) => (
                  <div key={shippingMethod.id} className="flex flex-row justify-between mb-3">
                    <div>
                      <input className="form-check-input appearance-none rounded-full h-4 w-4 border border-gray-300 bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer"
                        type="radio"
                        name="shipping_type"
                        id={`shipping_method_${shippingMethod.id}`}
                        value={shippingMethod.id}
                        defaultChecked={form[FIELD_NAMES.shippingMethod] === shippingMethod.id}
                        onChange={({ target }) => handleShippingMethodChange(target.value)}
                      />
                      <label className="form-check-label inline-block text-gray-800" htmlFor={`shipping_method_${shippingMethod.id}`}>
                        {_capitalize(shippingMethod.name)}
                      </label>
                    </div>
                    <span className="text-left transform">{shippingMethod.estimated_delivery_window.maximum_date}</span>
                  </div>
                ))}
              </div>
              <p className='text-xs text-javelin-gray-dark text-opacity-30 mt-2' >
                Orders placed after 11 am Europe/Amsterdam timezone will ship the following business day.
              </p>
            </>
          )}
        </form>
      </div >
      <hr className="horizontal-line w-screen -mx-3 lg:hidden" />
    </>
  );
};

export default BillingAddress;
